<template>
  <m-card
    no-padding
    list
    class="grid-actions"
  >
    <template
      v-for="action in gridPageService.createActions"
      :key="action.gridPageType"
    >
      <m-dropdown
        v-if="action.children"
        block
        placement="topRight"
        trigger="hover"
        :title="action.label"
      >
        <m-card-item
          :icon="action.icon"
          :loading="loading === action.gridPageType"
          @click="add(action)"
        >
          {{ action.label }}
          <plan-tag
            v-if="action.gridPageType === gridPageType.chart && chartGated"
            :feature-flag="featureFlag.customCharts"
            class="_plan-tag"
          />
          <template #right>
            <m-icon
              type="right"
              size="11"
              :color="$colors.grey.lighten2"
            />
          </template>
        </m-card-item>
        <template
          v-if="!(action.gridPageType === gridPageType.chart && chartGated)"
          #overlay
        >
          <m-card
            no-padding
            list
            :style="{ minWidth: '14rem' }"
          >
            <m-card-item
              v-for="child in action.children"
              :key="child.key"
              :icon="child.icon"
              :loading="loading === child.key"
              @click="add(child)"
            >
              {{ child.label }}
            </m-card-item>
          </m-card>
        </template>
      </m-dropdown>
      <m-card-item
        v-else
        :icon="action.icon"
        :loading="loading === action.gridPageType"
        @click="add(action)"
      >
        {{ action.label }}
      </m-card-item>
    </template>
  </m-card>
</template>

<script setup>
import PlanTag from '@/components/plans/PlanTag.vue';
import useGridPage from '@/composables/grid-page/grid-page';
import useInlineEditing from '@/composables/inline-editing';
import useSnackBar from '@/composables/snackbar';
import { computed, ref } from 'vue';
import { featureFlag, gridPageType } from 'shared/constants.json';
import { logCatch } from '@/lib/logger/logger';
import { shouldShowFeatureGate, showFeatureGate } from '@/lib/feature-gate';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

const emit = defineEmits(['close']);

const props = defineProps({
  gridRow: {
    type: Object,
    required: true,
  },
  gridPage: {
    type: Object,
    required: true,
  },
});

const inlineEditingSvc = useInlineEditing();
const snackBar = useSnackBar();
const route = useRoute();
const router = useRouter();
const store = useStore();

const gridPageService = useGridPage();

const chartGated = computed(() => shouldShowFeatureGate(featureFlag.customCharts, store));

const loading = ref('');
function add(action) {
  if (action.gridPageType === gridPageType.chart && showFeatureGate(featureFlag.customCharts, store)) {
    emit('close');
    return;
  }
  if (Array.isArray(action.children) && action.children.length > 0) {
    return;
  }
  loading.value = action.key ?? action.gridPageType;
  gridPageService.addGridPageTile({
    type: action.gridPageType,
    gridPageRow: { uid: props.gridRow.uid, gridPage: { uid: props.gridPage.uid } },
    ...(action.tileArgs ?? {}),
  }).then((res) => {
    if (action.gridPageType === gridPageType.chart) {
      router.push({ query: { ...route.query, chartTile: String(res.uid) } });
    } else if (![gridPageType.singleGoal, gridPageType.iframe, gridPageType.media].includes(action.gridPageType)) {
      inlineEditingSvc.set(res.uid);
    }
  }).catch(logCatch(() => { snackBar.error(); }))
    .finally(() => {
      loading.value = '';
    });
  emit('close');
}

</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  ._plan-tag {
    margin-left: 0.6rem;
  }
</style>
